import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../providers/AuthProvider";
import { useSession } from "../providers/SessionProvider";
import useExperimentTracking from "./useExperimentTracking";
import useExperimentPreviewState from "./useExperimentPreviewState";
import { getCanonicalAuthUserId } from "../services/authIdentity";
import {
  getActiveExperimentContext,
  getExperimentAssignmentRevisionAt,
  getExperimentByKey,
  getExperimentPreviewOverride,
  getExperimentVariantByKey,
  resolveExperimentAssignment,
  resolveStickyExperimentAssignment,
} from "../services/experiments";

const useExperiment = (
  experimentKey,
  {
    subjectId = "",
    memberId = "",
    autoPersist = true,
    autoTrackExposure = false,
    exposureMetricKey = "exposure",
    exposureProperties = null,
    enrollmentProperties = null,
    eligibleVariantKeys = null,
    requireStoredAssignment = false,
    surface = "",
  } = {},
) => {
  const configuredExperiment = useMemo(
    () => getExperimentByKey(experimentKey),
    [experimentKey],
  );
  const { currentUser } = useAuth() || {};
  const currentUserId = getCanonicalAuthUserId(currentUser);
  const { sessionId = "" } = useSession() || {};
  const {
    rememberExperimentVariant = () => null,
    trackExperimentMetric = () => null,
  } = useExperimentTracking();
  const previewState = useExperimentPreviewState();
  const [assignmentClock, setAssignmentClock] = useState(() => Date.now());
  const experiment = useMemo(
    () => getExperimentAssignmentRevisionAt(configuredExperiment, new Date(assignmentClock)),
    [configuredExperiment, assignmentClock],
  );
  // Ingest rejects exposures that arrive before their assignment.
  const assignmentTrackingRef = useRef(null);
  const eligibleVariantKeySignature = Array.isArray(eligibleVariantKeys)
    ? JSON.stringify(
        eligibleVariantKeys
          .map((variantKey) => String(variantKey || "").trim())
          .filter(Boolean),
      )
    : "";
  const normalizedEligibleVariantKeys = useMemo(
    () =>
      eligibleVariantKeySignature
        ? JSON.parse(eligibleVariantKeySignature)
        : null,
    [eligibleVariantKeySignature],
  );

  useEffect(() => {
    if (!experiment) return undefined;
    const now = Date.now();
    const nextBoundary = [
      experiment.effectiveStartAt || experiment.valueMetrics?.effectiveStartAt,
      experiment.effectiveEndAt || experiment.valueMetrics?.effectiveEndAt,
    ]
      .map((value) => new Date(value || 0).getTime())
      .filter((value) => Number.isFinite(value) && value > now)
      .sort((left, right) => left - right)[0];
    if (!nextBoundary) return undefined;
    const timer = setTimeout(
      () => setAssignmentClock(Date.now()),
      Math.min(nextBoundary - now + 50, 2_147_483_647),
    );
    return () => clearTimeout(timer);
  }, [
    assignmentClock,
    experiment,
    experiment?.effectiveEndAt,
    experiment?.effectiveStartAt,
    experiment?.valueMetrics?.effectiveEndAt,
    experiment?.valueMetrics?.effectiveStartAt,
  ]);

  const deterministicAssignment = useMemo(
    () =>
      resolveExperimentAssignment(experiment, {
        subjectId,
        memberId,
        userId: currentUserId,
        sessionId,
        date: new Date(assignmentClock),
        eligibleVariantKeys: normalizedEligibleVariantKeys,
      }),
    [
      assignmentClock,
      experiment,
      subjectId,
      memberId,
      currentUserId,
      sessionId,
      normalizedEligibleVariantKeys,
    ],
  );
  const storedAssignmentContext = useMemo(() => {
    if (!deterministicAssignment?.subjectId) return null;
    const options =
      deterministicAssignment.unit === "session"
        ? { sessionId: deterministicAssignment.subjectId }
        : { userId: deterministicAssignment.subjectId };
    return getActiveExperimentContext(experimentKey, options);
  }, [
    deterministicAssignment?.subjectId,
    deterministicAssignment?.unit,
    experimentKey,
  ]);
  const assignment = useMemo(
    () =>
      resolveStickyExperimentAssignment(
        experiment,
        requireStoredAssignment && !storedAssignmentContext
          ? null
          : deterministicAssignment,
        storedAssignmentContext,
        { eligibleVariantKeys: normalizedEligibleVariantKeys },
      ),
    [
      deterministicAssignment,
      experiment,
      normalizedEligibleVariantKeys,
      requireStoredAssignment,
      storedAssignmentContext,
    ],
  );

  const previewOverride = useMemo(
    () => getExperimentPreviewOverride(experimentKey, previewState),
    [experimentKey, previewState],
  );
  const isActiveExperiment = experiment?.status === "active";
  const assignedVariantKey = assignment?.variantKey || "";
  const fallbackVariantKey =
    isActiveExperiment && !assignedVariantKey
      ? String(experiment?.fallbackVariantKey || "").trim()
      : "";
  const variantKey =
    previewOverride?.variantKey || assignedVariantKey || fallbackVariantKey;
  const variant = useMemo(
    () =>
      previewOverride?.variant ||
      getExperimentVariantByKey(experimentKey, variantKey, experiment),
    [experiment, experimentKey, previewOverride?.variant, variantKey],
  );
  const isPreviewOverride = Boolean(previewOverride?.variantKey);
  const assignmentUnit = assignment?.unit || experiment?.assignment?.unit || "";
  const assignmentSubjectOptions = useMemo(() => {
    const assignmentSubjectId = assignment?.subjectId || "";
    if (assignment?.unit === "member") {
      return {
        subjectMemberId: assignmentSubjectId || null,
        subjectUserId: null,
        subjectSessionId: null,
      };
    }
    if (assignment?.unit === "user") {
      return {
        subjectMemberId: null,
        subjectUserId: assignmentSubjectId || null,
        subjectSessionId: null,
      };
    }
    return {
      subjectMemberId: null,
      subjectUserId: null,
      subjectSessionId: assignmentSubjectId || null,
    };
  }, [assignment?.subjectId, assignment?.unit]);
  const hasTrackingSubject = useMemo(() => {
    if (!assignmentUnit) return true;
    if (assignmentUnit === "session") {
      return Boolean(assignmentSubjectOptions.subjectSessionId);
    }
    if (assignmentUnit === "user") {
      return Boolean(assignmentSubjectOptions.subjectUserId);
    }
    if (assignmentUnit === "member") {
      return Boolean(assignmentSubjectOptions.subjectMemberId);
    }
    return true;
  }, [assignmentSubjectOptions, assignmentUnit]);
  const assignmentProperties = useMemo(
    () => ({
      ...(enrollmentProperties || {}),
      revisionId:
        assignment?.revisionId ||
        experiment?.assignment?.revisionId ||
        experiment?.revisionId ||
        `${experiment?.key || experimentKey}_r1`,
      assignmentVersion: assignment?.assignmentVersion || "",
      assignmentUnit: assignment?.unit || "session",
      assignmentCadence: assignment?.cadence || "static",
      assignmentBucketKey: assignment?.bucketKey || "static",
      eligibilityStatus: hasTrackingSubject ? "eligible" : "ineligible",
      eligibleVariantKeys: assignment?.eligibleVariantKeys || [],
    }),
    [
      enrollmentProperties,
      assignment?.assignmentVersion,
      assignment?.revisionId,
      assignment?.bucketKey,
      assignment?.cadence,
      assignment?.unit,
      assignment?.eligibleVariantKeys,
      hasTrackingSubject,
      experiment?.assignment?.revisionId,
      experiment?.revisionId,
      experiment?.key,
      experimentKey,
    ],
  );

  useEffect(() => {
    if (
      isPreviewOverride ||
      !isActiveExperiment ||
      !autoPersist ||
      !experiment?.key ||
      !assignedVariantKey ||
      !hasTrackingSubject
    ) {
      return;
    }

    rememberExperimentVariant({
      experimentKey: experiment.key,
      variantKey: assignedVariantKey,
      source: "assignment",
      surface,
      ...assignmentSubjectOptions,
      metadata: {
        revisionId: assignment?.revisionId || experiment?.revisionId || "",
        bucketKey: assignment?.bucketKey || "static",
        cadence: assignment?.cadence || "static",
        assignmentVersion: assignment?.assignmentVersion || "",
        timeZone:
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          "America/Los_Angeles",
        unit: assignment?.unit || "session",
      },
    });
    assignmentTrackingRef.current = {
      assignmentKey: `${experiment.key}:${assignedVariantKey}:${assignment?.subjectId || ""}`,
      promise: Promise.resolve(
        trackExperimentMetric({
          experimentKey: experiment.key,
          revisionId: assignment?.revisionId,
          variantKey: assignedVariantKey,
          metricKey: "assignment",
          metricType: "assignment",
          surface,
          source: "assignment",
          once: {
            scope:
              assignment?.unit === "user" || assignment?.unit === "member"
                ? "user"
                : "session",
            key: [
              experiment.key,
              assignedVariantKey,
              assignment?.assignmentVersion || "",
              assignment?.bucketKey || "static",
            ].join(":"),
          },
          ...assignmentSubjectOptions,
          properties: assignmentProperties,
        }),
      ).catch(() => null),
    };
  }, [
    autoPersist,
    isActiveExperiment,
    experiment?.key,
    experiment?.revisionId,
    assignedVariantKey,
    isPreviewOverride,
    hasTrackingSubject,
    rememberExperimentVariant,
    surface,
    assignmentSubjectOptions,
    assignment?.bucketKey,
    assignment?.cadence,
    assignment?.assignmentVersion,
    assignment?.revisionId,
    assignment?.unit,
    assignmentProperties,
    trackExperimentMetric,
  ]);

  useEffect(() => {
    if (
      isPreviewOverride ||
      !isActiveExperiment ||
      !autoTrackExposure ||
      !experiment?.key ||
      !assignedVariantKey ||
      !hasTrackingSubject
    ) {
      return;
    }

    const assignmentKey = `${experiment.key}:${assignedVariantKey}:${assignment?.subjectId || ""}`;
    const assignmentPromise =
      assignmentTrackingRef.current?.assignmentKey === assignmentKey
        ? assignmentTrackingRef.current.promise
        : Promise.resolve();
    void assignmentPromise
      .then(() =>
        trackExperimentMetric({
          experimentKey: experiment.key,
          revisionId: assignment?.revisionId,
          variantKey: assignedVariantKey,
          metricKey: exposureMetricKey,
          metricType: "exposure",
          surface,
          once: {
            scope:
              assignment?.unit === "user" || assignment?.unit === "member"
                ? "user"
                : "session",
            key: [
              experiment.key,
              surface || "unknown_surface",
              exposureMetricKey,
              assignedVariantKey,
              assignment?.bucketKey || "static",
            ].join(":"),
          },
          ...assignmentSubjectOptions,
          properties: {
            ...assignmentProperties,
            ...(exposureProperties || {}),
          },
        }),
      )
      .catch(() => {});
  }, [
    autoTrackExposure,
    isActiveExperiment,
    experiment?.key,
    experiment?.revisionId,
    assignedVariantKey,
    isPreviewOverride,
    hasTrackingSubject,
    exposureMetricKey,
    surface,
    assignment?.unit,
    assignment?.bucketKey,
    assignment?.cadence,
    assignment?.assignmentVersion,
    assignmentProperties,
    exposureProperties,
    assignmentSubjectOptions,
    trackExperimentMetric,
  ]);

  return {
    experiment,
    assignment,
    assignedVariantKey,
    fallbackVariantKey,
    isFallback: Boolean(fallbackVariantKey && !isPreviewOverride),
    isPreviewOverride,
    previewOverride,
    variant,
    variantKey,
    isVariant: (candidateKey = "") => variantKey === candidateKey,
    rememberVariant: (nextVariantKey, extra = {}) =>
      rememberExperimentVariant({
        experimentKey,
        variantKey: nextVariantKey,
        surface,
        ...assignmentSubjectOptions,
        ...extra,
      }),
    waitForAssignment: () => {
      const key = `${experiment?.key}:${assignedVariantKey}:${assignment?.subjectId || ""}`;
      return assignmentTrackingRef.current?.assignmentKey === key
        ? assignmentTrackingRef.current.promise
        : Promise.resolve();
    },
    trackMetric: ({
      metricKey,
      metricType = "action",
      numericValue = null,
      properties = {},
      once = null,
      persistVariant = false,
      variantKey: overrideVariantKey = variantKey,
    } = {}) => {
      const trackedVariantKey = String(overrideVariantKey || "").trim();
      if (!isPreviewOverride && (!assignment || !trackedVariantKey)) {
        return null;
      }
      return trackExperimentMetric({
        experimentKey,
        revisionId: assignment?.revisionId,
        variantKey: trackedVariantKey,
        metricKey,
        metricType,
        numericValue,
        properties: {
          ...assignmentProperties,
          ...properties,
        },
        once,
        persistVariant,
        surface,
        ...assignmentSubjectOptions,
      });
    },
  };
};

export default useExperiment;
